import { useMemo } from 'react';
import MetricCard from '../shared/MetricCard';
import Badge from '../shared/Badge';
import AgingBadge from '../shared/AgingBadge';
import VendorTag from '../shared/VendorTag';
import DataTable from '../shared/DataTable';
import EmptyState from '../shared/EmptyState';
import { formatMoney, formatDate } from '../../utils/formatters';

export default function OrderedNeverReceived({ data }) {
  const items = data?.items || [];
  const totalCost = data?.total_cost_usd || items.reduce((s, o) => s + (o.cost_usd || 0), 0);

  const columns = useMemo(() => [
    {
      key: 'part_name',
      label: 'Part',
      render: (row) => (
        <div>
          <div className="font-bold text-sm">{row.part_name}</div>
          <div className="text-xs text-gray-400">{row.part_number}</div>
        </div>
      ),
    },
    {
      key: 'ro_number',
      label: 'RO / Customer',
      render: (row) => (
        <div>
          <div className="font-semibold text-sm">#{row.ro_number}</div>
          <div className="text-xs text-gray-400">{row.customer_name}</div>
        </div>
      ),
    },
    {
      key: 'vendor_name',
      label: 'Vendor',
      render: (row) => <VendorTag name={row.vendor_name} />,
    },
    {
      key: 'order_date',
      label: 'Ordered',
      render: (row) => <span className="text-xs">{formatDate(row.order_date)}</span>,
    },
    {
      key: 'cost_usd',
      label: 'Cost',
      align: 'right',
      render: (row) => <span className="font-bold">{formatMoney(row.cost_usd)}</span>,
    },
    {
      key: 'ro_status',
      label: 'RO Status',
      render: (row) => <Badge severity="critical" text={row.ro_status || 'Posted'} />,
    },
    {
      key: 'days_since_ordered',
      label: 'Age',
      align: 'right',
      render: (row) => <AgingBadge days={row.days_since_ordered || 0} />,
    },
  ], []);

  return (
    <div>
      <div className="flex gap-3 mb-6 flex-wrap">
        <MetricCard
          label="Unreceived Parts"
          value={items.length.toString()}
          accent="#E65100"
          icon={'\uD83D\uDCE6'}
        />
        <MetricCard
          label="Cost Exposure"
          value={formatMoney(totalCost)}
          sub="On posted ROs"
          accent="#C62828"
          icon={'\uD83D\uDCB0'}
        />
      </div>

      <div className="text-xs text-gray-500 mb-4 px-3 py-2 bg-red-50 rounded-lg border border-red-200">
        Parts marked as "Ordered" on a purchase order but never marked as "Received", where the associated repair order has already been posted (billed).
      </div>

      {items.length > 0 ? (
        <DataTable columns={columns} data={items} />
      ) : (
        <EmptyState icon="&#10003;" text="All ordered parts have been received" />
      )}
    </div>
  );
}
